import { buildCategoryColorMap } from "./colors";
import { toEnergyAnalysisLabel } from "./labels";

import type {
  EnergyAnalysisCategoryTotal,
  EnergyAnalysisRow,
  EnergyAnalysisSplitDimension,
  EnergyAnalysisTrendSeries,
  EnergyAnalysisYearlyTableRow,
} from "../model/types";

export const collectDimensionKeys = (rows: EnergyAnalysisRow[], dimension: EnergyAnalysisSplitDimension) =>
  [...new Set(rows.map((row) => row[dimension]))]
    .sort()
    .filter((key) => sumValues(rows.filter((row) => row[dimension] === key)) > 0);

export const collectYears = (rows: EnergyAnalysisRow[]) =>
  [...new Set(rows.map((row) => row.year))].sort((left, right) => left - right);

export const sumValues = (rows: EnergyAnalysisRow[]) => rows.reduce((total, row) => total + row.value, 0);

export const buildCategoryTotals = (
  rows: EnergyAnalysisRow[],
  dimension: EnergyAnalysisSplitDimension
): EnergyAnalysisCategoryTotal[] => {
  const keys = collectDimensionKeys(rows, dimension);
  const colors = buildCategoryColorMap(keys);
  const totalValue = sumValues(rows);
  return keys
    .map((key) => {
      const value = sumValues(rows.filter((row) => row[dimension] === key));
      const color = colors[key];
      return {
        color: color ?? "var(--color-foreground-primary)",
        key,
        label: toEnergyAnalysisLabel(key),
        share: totalValue === 0 ? 0 : value / totalValue,
        value,
      };
    })
    .sort((left, right) => right.value - left.value);
};

export const buildTrendSeries = (
  rows: EnergyAnalysisRow[],
  dimension: EnergyAnalysisSplitDimension
): { series: EnergyAnalysisTrendSeries[]; years: number[] } => {
  const years = collectYears(rows);
  const keys = collectDimensionKeys(rows, dimension);
  const colors = buildCategoryColorMap(keys);
  const series = keys
    .map((key) => {
      const color = colors[key];
      return {
        color: color ?? "var(--color-foreground-primary)",
        data: years.map((year) => sumValues(rows.filter((row) => row.year === year && row[dimension] === key))),
        name: toEnergyAnalysisLabel(key),
      };
    })
    .sort((left, right) => (right.data[0] ?? 0) - (left.data[0] ?? 0));
  return { series, years };
};

export const buildYearlyTableRows = (
  rows: EnergyAnalysisRow[],
  dimension: EnergyAnalysisSplitDimension
): EnergyAnalysisYearlyTableRow[] => {
  const years = collectYears(rows);
  const keys = collectDimensionKeys(rows, dimension);
  return years.map((year) => {
    const yearRows = rows.filter((row) => row.year === year);
    const yearlyRow: EnergyAnalysisYearlyTableRow = {
      id: `${year}`,
      rowLabel: year,
      value: sumValues(yearRows),
      year,
    };
    keys.forEach((key) => {
      yearlyRow[key] = sumValues(yearRows.filter((row) => row[dimension] === key));
    });
    return yearlyRow;
  });
};
